import {DraftProjectStatus} from '@contentlab/api/project/types';
import {ROUTES} from '@contentlab/config/routesConstants';
import {
  BaseProject,
  contentLabActions,
} from '@contentlab/stores/contentLabStore';
import {FreeTrialEndModalReason} from '@contentlab/stores/freeTrialEndModalStore';
import getDefaultProjectName from '@contentlab/utils/getDefaultProjectName';
import resolveCreditAccessAction, {
  BLOCKED_CREDIT_STATUSES,
  CREDIT_ACCESS_ACTION,
} from '@contentlab/utils/resolveCreditAccessAction';
import {setDraftProjectId} from '@/utils/LtidStorage';
import getActiveSubscription from '@/views/content-studio/utils/getActiveSubscription';
import useCurrentSubscriptionQuery from '@/hooks/content-studio/use-current-subscription-query';
import useNavigation from '@/hooks/use-navigation';
import {isTrialPlan} from '../../views/contentlab/pricing/utils';
import useTrialUpgradeModal from '../use-trial-upgrade-modal/useTrialUpgradeModal';

type GenerateProjectArgs = {
  getRequiredCredits: () => number;
  setIsSubmitting: (isSubmitting: boolean) => void;
  draftProject: BaseProject;
  defaultProjectName: string;
  updateProject: (status: DraftProjectStatus) => Promise<void>;
  resetProject: () => void;
};

export default function useGenerateProject({
  getRequiredCredits,
  setIsSubmitting,
  draftProject,
  defaultProjectName,
  updateProject,
  resetProject,
}: GenerateProjectArgs): () => Promise<void> {
  const {navigate} = useNavigation();
  const openTrialUpgradeModal = useTrialUpgradeModal();
  const {data, isLoading} = useCurrentSubscriptionQuery();
  const activeSubscription = getActiveSubscription(data?.subscriptions);
  return async () => {
    const requiredCredits = getRequiredCredits();
    const creditAccessAction = await resolveCreditAccessAction(requiredCredits);
    const {projectId} = draftProject;
    if (BLOCKED_CREDIT_STATUSES.includes(creditAccessAction)) {
      if (isTrialPlan(activeSubscription)) {
        openTrialUpgradeModal({
          source: 'generate_project_no_credits',
          reason: FreeTrialEndModalReason.CREDIT_END,
        });
      } else {
        contentLabActions.setCreditEndModalState(creditAccessAction);
      }
      setIsSubmitting(false);
      if (projectId) {
        setDraftProjectId(projectId);
      }
      return;
    }

    if (draftProject.projectName === '') {
      contentLabActions.updateDraftDubbingProjectName(
        getDefaultProjectName(defaultProjectName)
      );
    }
    await updateProject(DraftProjectStatus.EXECUTE);
    resetProject();

    if (creditAccessAction === CREDIT_ACCESS_ACTION.EXACT_CREDITS) {
      if (isTrialPlan(activeSubscription)) {
        openTrialUpgradeModal({
          source: 'generate_project_no_credits',
          reason: FreeTrialEndModalReason.CREDIT_END,
        });
      } else {
        localStorage.setItem(`display_zero_credits_modal`, 'true');
      }
    }
    localStorage.setItem(`showReady_${projectId}`, 'true');
    localStorage.setItem(`notifyProjectReady_${projectId}`, 'true');
    navigate(`${ROUTES.CONTENT_LAB}${ROUTES.CONTENT_LAB_PROJECT}/${projectId}`);
  };
}
